// Scan a new RF sensor: say what it is, do what the app asks, name it, save.
//
// Every type is an RF sensor — one `RfDevice` row; the choice only sets RfType
// and decides which events to ask for. The logic is core/devices/rfScan; this
// is the screen around it. Both shells, both entry points (the /rf-devices page
// and the receiver's own screen) through RfDevicesPanel.
//
// ⚠️ NOTHING IS SENT. The scan only listens; the server runs any actions.
// ⚠️ A NEW SENSOR IS SAVED THROUGH THE CODES. `SaveRfCode` files a code under
// its transmitter and creates that sensor's row on first sight, returning its
// RfDeviceId — so the codes go first and the sensor's name/type/room second.

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Button, Alert, TextField, MenuItem, Switch, FormControlLabel,
  Dialog, DialogTitle, DialogContent, DialogActions, LinearProgress,
  useMediaQuery, useTheme,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { saveRfCode, saveRfDevice, replaceRfTransmitter } from '../../core/api/api';
import useRfScan from '../../core/hooks/useRfScan';
import { RF_TYPES, rfType } from '../../core/devices/rfTypes';
import { binaryOf } from '../../core/devices/rfCodes';
import { rfNumber } from '../../core/devices/rfIdentify';
import { FIT_DIALOG, PHONE_SHEET } from './fitDialog';
import {
  SCAN_PRIMARY_TYPES, currentStep, scanDone, scanTransmitter, namedCodeIndex,
} from '../../core/devices/rfScan';

function TypeButton({ t, onClick, big }) {
  return (
    <Button
      variant={big ? 'contained' : 'outlined'}
      onClick={onClick}
      sx={{
        flex: big ? '1 1 140px' : '1 1 120px',
        py: big ? 2 : 1,
        flexDirection: 'column',
        textTransform: 'none',
        gap: 0.5,
      }}
    >
      <Box component="span" sx={{ fontSize: big ? 30 : 20, lineHeight: 1 }}>{t.icon}</Box>
      <Box component="span" sx={{ fontWeight: 700 }}>{t.label}</Box>
    </Button>
  );
}

/**
 * @param open         show the dialog
 * @param receiverUno  listen to this receiver only ('' = every receiver)
 * @param locationId   saved on the sensor; its rooms are `rooms`
 * @param devices      GetRfDevices rows — codes already named are refused
 * @param onSaved      ({ RfDeviceId, transmitterId }) after a successful save
 * @param initialType  skip the type question (e.g. the signature already says "door")
 * @param replace      an RfDevice row: scan its REPLACEMENT unit instead of a new
 *                     sensor. The sensor keeps its RF number and everything on it;
 *                     only the hardware changes (ReplaceRfTransmitter).
 */
export default function RfScanDialog({
  open, receiverUno = '', locationId = 0, rooms = [], devices = [], onClose, onSaved, initialType: typeProp = '',
  replace = null,
}) {
  // A replacement is the same KIND of sensor — ask for the same events.
  const initialType = replace ? (replace.RfType || 'other') : typeProp;
  const theme = useTheme();
  const narrow = useMediaQuery(theme.breakpoints.down('sm'));
  const known = useMemo(() => namedCodeIndex(devices), [devices]);
  const {
    scan, begin, skip, redo, reset,
  // Listens only while a scan exists — and closing resets it — so a closed
  // dialog holds no subscription.
  } = useRfScan(receiverUno, known);

  // Opened already knowing the type: go straight to the steps.
  useEffect(() => {
    if (open && initialType && !scan) begin(initialType);
  }, [open, initialType]); // eslint-disable-line react-hooks/exhaustive-deps

  const [moreTypes, setMoreTypes] = useState(false);
  const [name, setName] = useState('');
  const [roomId, setRoomId] = useState(0);
  const [notify, setNotify] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const [replaced, setReplaced] = useState(null);

  const close = () => {
    reset(); setMoreTypes(false); setName(''); setRoomId(0); setNotify(true); setError(''); setReplaced(null);
    onClose();
  };

  const step = currentStep(scan);
  const done = scanDone(scan);
  const tx = done ? scanTransmitter(scan) : null;
  const type = scan ? rfType(scan.type) : null;

  const save = async () => {
    if (!scan || !tx?.ok) return;
    setBusy(true); setError('');
    try {
      let rfDeviceId = 0;
      // One at a time: the first creates the sensor row, the rest join it.
      // eslint-disable-next-line no-restricted-syntax
      for (const c of scan.captured) {
        // eslint-disable-next-line no-await-in-loop
        const r = await saveRfCode({
          REMOTEVALUE: String(c.code),
          RemoteBinaryValue: c.binary || binaryOf(c.code),
          UniqueNumber: c.receiverUno || receiverUno || '',
          DeviceAction: c.meaning,
          DeviceName: name.trim(),
        });
        rfDeviceId = rfDeviceId || Number(r?.data?.RfDeviceId) || 0;
      }
      const receiver = scan.captured[0]?.receiverUno || receiverUno || '';
      const body = {
        Name: name.trim(),
        RfType: scan.type,
        LocationID: Number(locationId) || 0,
        RoomID: Number(roomId) || 0,
        Notify: notify ? 1 : 0,
        Active: 1,
      };
      const r = await saveRfDevice(rfDeviceId
        ? { RfDeviceId: rfDeviceId, ...body }
        : { TransmitterId: tx.transmitterId, ReceiverUniqueNumber: receiver, ...body });
      const id = rfDeviceId || Number(r?.data?.RfDeviceId) || 0;
      const saved = { RfDeviceId: id, transmitterId: tx.transmitterId };
      close();
      onSaved(saved);
    } catch (e) {
      setError('Could not save. Check the connection and press Save again — the codes are still here.');
    } finally {
      setBusy(false);
    }
  };

  const doReplace = async () => {
    if (!scan || !tx?.ok || !replace) return;
    setBusy(true); setError('');
    try {
      const r = await replaceRfTransmitter(replace.RfDeviceId, scan.captured.map((c) => ({
        REMOTEVALUE: String(c.code),
        RemoteBinaryValue: c.binary || binaryOf(c.code),
        UniqueNumber: c.receiverUno || receiverUno || '',
        DeviceAction: c.meaning,
      })));
      if (!r?.data?.ok) throw new Error(r?.data?.error || 'not replaced');
      setReplaced(r.data);
    } catch (e) {
      const why = e?.response?.data?.error || e?.message || '';
      // A 404 HTML page means the server has not been updated yet.
      setError(e?.response?.status === 404 && !e?.response?.data?.error
        ? 'The server does not have Replace yet — deploy the backend first.'
        : `Could not replace${why ? `: ${why}` : ''}.`);
    } finally {
      setBusy(false);
    }
  };

  const notice = scan?.notice;

  return (
    <Dialog open={!!open} onClose={busy ? undefined : close} {...(narrow ? PHONE_SHEET : FIT_DIALOG)}>
      <DialogTitle>
        {/* eslint-disable-next-line no-nested-ternary */}
        {replace
          ? `Replace hardware — ${replace.Name || 'sensor'} (${rfNumber(replace.RfDeviceId)})`
          : (type ? `${type.icon} Scan ${type.label.toLowerCase()}` : 'Scan an RF sensor')}
      </DialogTitle>
      <DialogContent dividers>
        {/* ── 1. What is it ─────────────────────────────────────────────── */}
        {!scan && (
          <>
            <Typography variant="body2" color="text.secondary" className="fit-text" sx={{ mb: 1.5 }}>
              What are you adding? Every one of these is saved as an RF sensor.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {SCAN_PRIMARY_TYPES.map((k) => (
                <TypeButton key={k} big t={rfType(k)} onClick={() => begin(k)} />
              ))}
            </Box>
            {!moreTypes ? (
              <Button size="small" sx={{ mt: 1 }} onClick={() => setMoreTypes(true)}>More types…</Button>
            ) : (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.5 }}>
                {RF_TYPES.filter((t) => !SCAN_PRIMARY_TYPES.includes(t.key)).map((t) => (
                  <TypeButton key={t.key} t={t} onClick={() => begin(t.key)} />
                ))}
              </Box>
            )}
            <Alert severity="info" sx={{ mt: 2 }}>
              {receiverUno
                ? `Listening to receiver ${receiverUno}. Keep the sensor near it.`
                : 'Listening to every receiver. The one that hears the sensor is saved with it.'}
            </Alert>
          </>
        )}

        {/* ── 2. Do what it asks ────────────────────────────────────────── */}
        {scan && (
          <>
            <Box sx={{ mb: 1.5 }}>
              {scan.steps.map((s, i) => {
                const got = scan.captured.find((c) => c.meaning === s.meaning);
                const isNow = i === scan.step;
                return (
                  <Box key={s.meaning} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25, opacity: i > scan.step ? 0.5 : 1 }}>
                    {got
                      ? <CheckCircleIcon fontSize="small" color="success" />
                      : <RadioButtonUncheckedIcon fontSize="small" color={isNow ? 'primary' : 'disabled'} />}
                    <Typography variant="body2" sx={{ fontWeight: isNow ? 700 : 400, flex: 1 }}>
                      {s.meaning}
                      {s.optional && !got ? ' (optional)' : ''}
                    </Typography>
                    {got && (
                      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{got.code}</Typography>
                    )}
                    {!got && i < scan.step && (
                      <Typography variant="caption" color="text.secondary">skipped</Typography>
                    )}
                  </Box>
                );
              })}
            </Box>

            {step && (
              <Box className="fit-text" sx={{ p: 2, borderRadius: 2, border: 2, borderColor: 'primary.main', textAlign: 'center', mb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>{step.prompt}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Listening… it needs to be heard twice, so press or trigger it again if asked.
                </Typography>
                <LinearProgress sx={{ mt: 1 }} />
              </Box>
            )}

            {notice?.kind === 'once' && step && (
              <Alert severity="info" sx={{ mb: 1 }}>
                {`Heard ${notice.code} once — do it again to confirm it.`}
              </Alert>
            )}
            {notice?.kind === 'known' && (
              <Alert severity="warning" sx={{ mb: 1 }}>
                {`That code (${notice.code}) already belongs to “${notice.name}”${notice.meaning ? ` — ${notice.meaning}` : ''}. `}
                Edit that sensor instead, or trigger a different one.
              </Alert>
            )}
            {notice?.kind === 'captured' && step && (
              <Alert severity="success" sx={{ mb: 1 }}>
                {`${notice.meaning} = ${notice.code}`}
              </Alert>
            )}

            {/* ── 3. Name it ────────────────────────────────────────────── */}
            {done && tx && !tx.ok && (
              <Alert severity="error" sx={{ mb: 1 }}>
                {tx.transmitters.length
                  ? `These codes come from ${tx.transmitters.length} different transmitters (${tx.transmitters.join(', ')}), so they would be saved as separate sensors. Either two devices were triggered, or this sensor does not split its code the way the app expects — start over, and if it happens again note the codes for the developer.`
                  : 'No code was captured.'}
              </Alert>
            )}
            {/* ── 3b. Replace: the new unit takes over the SAME sensor ──── */}
            {done && tx?.ok && replace && !replaced && (
              <Alert severity="warning" sx={{ mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {`New unit hw ${tx.transmitterId} takes over ${rfNumber(replace.RfDeviceId)} “${replace.Name || ''}”.`}
                </Typography>
                <Typography variant="body2">
                  {`The old unit hw ${replace.TransmitterId} is detached. The name, room, who is told, the history and any switch actions stay with the sensor.`}
                </Typography>
              </Alert>
            )}
            {replaced && (
              <Alert severity={replaced.unmatched?.length ? 'warning' : 'success'} sx={{ mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {`Replaced — ${rfNumber(replace.RfDeviceId)} is now hw ${replaced.newTransmitterId}.`}
                </Typography>
                <Typography variant="body2">
                  {`${replaced.mappingsMoved || 0} switch action(s) moved to the new codes.`}
                </Typography>
                {!!replaced.unmatched?.length && (
                  <Typography variant="body2">
                    {`Not carried over (the new unit did not send them): ${replaced.unmatched.join(', ')}. Their switch actions stay on the old codes — re-create them if needed.`}
                  </Typography>
                )}
              </Alert>
            )}
            {done && tx?.ok && !replace && (
              <>
                <Alert severity="success" sx={{ mb: 1.5 }}>
                  {`Got it — sensor ${tx.transmitterId}, ${scan.captured.length} code${scan.captured.length === 1 ? '' : 's'}. Name it and save.`}
                </Alert>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <TextField
                    autoFocus size="small" label="Name" value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={`${type.label}, e.g. Main door`}
                    sx={{ flex: '1 1 220px' }}
                  />
                  {rooms.length > 0 && (
                    <TextField
                      select size="small" label="Room" value={roomId}
                      onChange={(e) => setRoomId(e.target.value)} sx={{ flex: '1 1 180px' }}
                    >
                      <MenuItem value={0}>No room</MenuItem>
                      {rooms.map((r) => <MenuItem key={r.RoomID} value={r.RoomID}>{r.RoomName}</MenuItem>)}
                    </TextField>
                  )}
                </Box>
                <FormControlLabel
                  sx={{ mt: 1 }}
                  control={<Switch checked={notify} onChange={(e) => setNotify(e.target.checked)} />}
                  label="Notify on its events"
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  After saving, the sensor&apos;s settings open — choose who is told and send a test.
                </Typography>
              </>
            )}
            {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 0.5 }}>
        {scan && !replaced && <Button onClick={reset} disabled={busy}>Start over</Button>}
        {scan && !replaced && scan.step > 0 && !busy && <Button onClick={redo}>Redo last step</Button>}
        {step?.optional && <Button onClick={skip}>Skip</Button>}
        <Box sx={{ flex: 1 }} />
        <Button onClick={close} disabled={busy}>Cancel</Button>
        {done && tx?.ok && !replace && (
          <Button variant="contained" onClick={save} disabled={busy || !name.trim()}>
            Save
          </Button>
        )}
        {done && tx?.ok && replace && !replaced && (
          <Button variant="contained" color="warning" onClick={doReplace} disabled={busy}>
            Replace
          </Button>
        )}
        {replaced && (
          <Button variant="contained" onClick={() => { const r = replaced; close(); onSaved({ RfDeviceId: replace.RfDeviceId, transmitterId: r.newTransmitterId }); }}>
            Done
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
